import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, Sparkles, Eye, EyeOff, ExternalLink, Copy, KeyRound, FileText,
  Wand2, Check, Globe2, Link2, Github, Linkedin, Briefcase, GraduationCap,
  MessageSquareText, ShieldCheck, MapPin, User, SlidersHorizontal, AlertTriangle, ChevronDown,
  Puzzle,
} from "lucide-react";
import { CHROME_STORE_URL } from "../lib/extension";
import { PageContainer, PageHeader, Field, Input, Button, Loading, inputClass } from "../components/kit";
import LocationPicker from "../components/LocationPicker";
import RolePicker from "../components/RolePicker";
import ResumeModePicker from "../components/ResumeModePicker";
import ApplyModal from "../components/ApplyModal";

// One-click apply — candidate "apply profile" settings.
// The standard facts application forms ask for, saved once and reused to auto-fill. Mirrors
// what the Autopilot onboarding wizard collects, plus the optional bits, all editable here.

// Work eligibility — US/CA only (the markets Autopilot applies in); candidate can add both.
const WORK_COUNTRIES = [["US", "United States"], ["CA", "Canada"]];
const AUTH_STATUS = [
  ["citizen", "Citizen"],
  ["permanent_resident", "Permanent resident"],
  ["work_permit", "Work permit / visa"],
  ["need_sponsorship", "Requires sponsorship"],
];
const LOCATION_PREF = [
  ["", "Select…"], ["remote", "Remote"], ["hybrid", "Hybrid"], ["onsite", "On-site"], ["flexible", "Flexible"],
];
const NOTICE = [
  ["", "Select…"], ["immediate", "Immediate"], ["two_weeks", "2 weeks"], ["one_month", "1 month"],
  ["two_months", "2 months"], ["three_months", "3 months"],
];

// Curated, globally-applicable optional questions — the ones asked on basically every
// application, answered once and reused. (Company/role-specific questions are generated per
// application and reviewed in the apply flow, so they never live here.)
const CURATED_GLOBAL = [
  { q: "What languages do you speak fluently?", hint: "e.g. English, French" },
  { q: "Do you hold any professional certifications or licenses relevant to your work?", hint: "e.g. PMP, AWS, ISTQB — or “None”" },
  { q: "Do you have a valid driver's license?", hint: "Yes / No" },
  { q: "What is your level of proficiency with Microsoft Office?", hint: "e.g. Advanced" },
  { q: "What software, tools, and programming languages have you worked with?", hint: "Comma-separated" },
  { q: "Do you require any accommodations during the recruitment process?", hint: "Leave blank if none" },
  { q: "What is your reason for seeking a new opportunity?", hint: "1–2 sentences" },
];
const norm = (s) => (s || "").trim().toLowerCase();
const CURATED_SET = new Set(CURATED_GLOBAL.map((c) => norm(c.q)));
// Per-application / company-specific answers the apply engine saved — reviewed at apply time,
// never shown here. Primarily source==="ai"; the regex is a safety net for stray entries.
const COMPANY_SPECIFIC_RX = /\b(terms|conditions|consent|privacy policy|non-?compete|acknowledge|previously\b.*\b(employed|interviewed|applied|worked)|at (this|our)\b)/i;
const isHiddenQA = (qa) => qa?.source === "ai" || COMPANY_SPECIFIC_RX.test(qa?.question || "");

const VD_GENDER = [["", "Prefer not to answer"], ["male", "Male"], ["female", "Female"], ["non_binary", "Non-binary"]];
const VD_RACE = [
  ["", "Prefer not to answer"], ["hispanic", "Hispanic or Latino"], ["white", "White"],
  ["black", "Black or African American"], ["asian", "Asian"],
  ["native_american", "Native American or Alaska Native"],
  ["pacific_islander", "Native Hawaiian or Pacific Islander"], ["two_or_more", "Two or more races"],
];
const VD_VETERAN = [["", "Prefer not to answer"], ["not_veteran", "Not a protected veteran"], ["veteran", "Protected veteran"]];
const VD_DISABILITY = [["", "Prefer not to answer"], ["no", "No, I don't have a disability"], ["yes", "Yes, I have a disability"]];

function Select({ value, onChange, options, className }) {
  return (
    <select value={value} onChange={onChange} className={`${inputClass} cursor-pointer ${className || ""}`}>
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );
}

function Toggle({ checked, onChange, label }) {
  const on = !!checked;
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)}
      className="flex items-center gap-3 py-1 group">
      <span className={`relative inline-flex w-11 h-6 rounded-full transition-colors flex-shrink-0 ${on ? "bg-[#0025D8]" : "bg-secondary border border-border"}`}>
        <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
          style={{ transform: on ? "translateX(20px)" : "translateX(0)" }} />
      </span>
      <span className="text-sm text-[#0F172A]">{label}</span>
    </button>
  );
}

// A collapsible full-width section row — the building block of the page.
//
// Full-width ROWS rather than a two-column card grid: collapsing one card in a grid leaves a hole
// beside its taller neighbour and the whole layout jumps. Rows collapse to a single line, so all
// eleven sections fit on one screen, and the width goes to the FIELDS inside (2–3 columns) rather
// than to whitespace.
//
// Collapsed, the row still answers "is this done, and what's in it?" via `summary` — otherwise
// collapsing just hides the information you came to check.
function Section({ icon: Icon, title, desc, action, children, className,
                   open, onToggle, summary, complete, collapsible = true }) {
  if (!collapsible) {
    return (
      <section className={`rounded-2xl border border-border bg-white p-5 ${className || ""}`}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#0025D8]/10 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-[#0025D8]" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-bold text-sm text-[#0F172A]">{title}</h2>
              {desc && <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{desc}</p>}
            </div>
          </div>
          {action}
        </div>
        {children}
      </section>
    );
  }
  return (
    <section className={`rounded-2xl border bg-white overflow-hidden transition-colors ${
      open ? "border-[#0025D8]/25 shadow-sm" : "border-border"} ${className || ""}`}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-[#F8FAFC] transition-colors">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          open ? "bg-[#0025D8]" : "bg-[#0025D8]/10"}`}>
          <Icon className={`w-4 h-4 ${open ? "text-white" : "text-[#0025D8]"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="font-display font-bold text-sm text-[#0F172A] truncate">{title}</h2>
            {complete === true && <Check className="w-3.5 h-3.5 text-[#0FA968] flex-shrink-0" />}
            {complete === false && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                style={{ background: "#E08A0015", color: "#B45309" }}>needs you</span>
            )}
          </div>
          {/* Collapsed: what's inside. Open: what the section is for. */}
          {!open && summary
            ? <p className="text-[11.5px] text-muted-foreground truncate mt-0.5">{summary}</p>
            : (open && desc ? <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{desc}</p> : null)}
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-5 vh-fade-in">
          {action && <div className="flex justify-end -mt-1 mb-3">{action}</div>}
          {children}
        </div>
      )}
    </section>
  );
}

// Quiet heading above a group of sections.
function GroupLabel({ children }) {
  return (
    <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground mt-6 mb-2 first:mt-0">
      {children}
    </h3>
  );
}

const AddBtn = ({ onClick, children }) => (
  <button onClick={onClick} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0025D8] hover:opacity-70 flex-shrink-0">
    <Plus className="w-3.5 h-3.5" /> {children}
  </button>
);

// Per-company portal logins VetraHub created (candidate's own email + a vaulted password).
function CompanyLogins(sectionProps) {
  const [accounts, setAccounts] = useState(null);
  const [shown, setShown] = useState({});
  const [busy, setBusy] = useState("");
  const [editKey, setEditKey] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    api.get("/apply/credentials").then(({ data }) => setAccounts(data.accounts || [])).catch(() => setAccounts([]));
  }, []);

  const toggle = async (a) => {
    const key = `${a.ats}:${a.tenant}`;
    if (shown[key]) { setShown((s) => { const n = { ...s }; delete n[key]; return n; }); return; }
    setBusy(key);
    try {
      const { data } = await api.post("/apply/credentials/reveal", { ats: a.ats, tenant: a.tenant });
      setShown((s) => ({ ...s, [key]: data }));
    } catch { toast.error("Couldn't reveal that login."); }
    finally { setBusy(""); }
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const startEdit = (a) => {
    const key = `${a.ats}:${a.tenant}`;
    setEditKey((k) => (k === key ? "" : key));
    setNewPw(""); setNewEmail(a.email || "");
  };
  const saveEdit = async (a) => {
    const key = `${a.ats}:${a.tenant}`;
    const email = newEmail.trim(), pw = newPw.trim();
    if (!email || !email.includes("@")) { toast.error("Enter a valid login email"); return; }
    const emailChanged = email !== (a.email || "");
    if (!emailChanged && !pw) { toast.error("Nothing to update — change the email or enter a new password."); return; }
    setSavingEdit(true);
    try {
      if (emailChanged) await api.post("/apply/credentials/update-email", { ats: a.ats, tenant: a.tenant, email });
      if (pw) await api.post("/apply/credentials/update-password", { ats: a.ats, tenant: a.tenant, password: pw });
      toast.success("Login updated — we'll use it on your next apply.");
      setAccounts((list) => list.map((x) => (x.ats === a.ats && x.tenant === a.tenant ? { ...x, email } : x)));
      setShown((s) => (s[key] ? { ...s, [key]: { ...s[key], email, ...(pw ? { password: pw } : {}) } } : s));
      setEditKey(""); setNewPw(""); setNewEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update that login.");
    } finally { setSavingEdit(false); }
  };

  if (accounts === null) return null;
  return (
    <Section {...sectionProps} icon={KeyRound} title="Company logins"
      desc="When Autopilot applies to a Workday employer, it creates you an account under your email with a secure password. Sign into any portal directly here.">
      {accounts.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <KeyRound className="w-4 h-4" /> No company logins yet — they appear after your first Workday apply.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {accounts.map((a) => {
            const key = `${a.ats}:${a.tenant}`;
            const r = shown[key];
            return (
              <div key={key} className="rounded-xl border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-[#0F172A] truncate">{a.company}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.platform} · {a.email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {a.login_url && (
                      <a href={a.login_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#0025D8] hover:underline">
                        Sign in <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <button onClick={() => toggle(a)} disabled={busy === key}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border border-border hover:bg-secondary disabled:opacity-50">
                      {r ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Show</>}
                    </button>
                    <button onClick={() => startEdit(a)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border hover:bg-secondary ${
                        editKey === key ? "border-[#0025D8] text-[#0025D8]" : "border-border"}`}>
                      <KeyRound className="w-3 h-3" /> Edit
                    </button>
                  </div>
                </div>
                {r && (
                  <div className="mt-2 pt-2 border-t border-border text-xs space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">Email</span>
                      <button onClick={() => copy(r.email)} title="Copy"
                        className="font-mono text-[#0F172A] hover:text-[#0025D8] inline-flex items-center gap-1 truncate max-w-[70%]">
                        <span className="truncate">{r.email}</span><Copy className="w-3 h-3 flex-shrink-0" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">Password</span>
                      <button onClick={() => copy(r.password)} title="Copy"
                        className="font-mono text-[#0F172A] hover:text-[#0025D8] inline-flex items-center gap-1 truncate max-w-[70%]">
                        <span className="truncate">{r.password}</span><Copy className="w-3 h-3 flex-shrink-0" />
                      </button>
                    </div>
                    {r.note && <p className="text-[11px] text-muted-foreground pt-1 leading-relaxed">{r.note}</p>}
                  </div>
                )}
                {editKey === key && (
                  <div className="mt-2 pt-2 border-t border-border space-y-2">
                    <input type="email" autoComplete="off" value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Login email" className={`${inputClass} text-xs`} />
                    <input type="text" autoComplete="off" value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveEdit(a); }}
                      placeholder="New password (leave blank to keep current)"
                      className={`${inputClass} font-mono text-xs`} />
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => saveEdit(a)} loading={savingEdit}><Save className="w-3.5 h-3.5" /> Save</Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditKey(""); setNewPw(""); setNewEmail(""); }}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// Section identity in one place: order, grouping, and how each row behaves when collapsed.
// `mode` is the default-open rule:
//   "always"     — Auto-submit: one toggle, and the setting with the largest consequence
//   "incomplete" — opens only while something is missing, then collapses to a summary line
//   "never"      — records rather than settings (populated by résumé import or by us), so they
//                  stay shut and stop the page being a scroll
const GROUPS = [
  { label: "Automation", keys: ["autosubmit"] },
  { label: "About you", keys: ["basics", "address", "links"] },
  { label: "Eligibility", keys: ["eligibility", "eeo", "prefs"] },
  { label: "Records", keys: ["work", "education", "bank", "logins"] },
];
const SECTION_MODE = {
  autosubmit: "always",
  resume: "always",
  // "incomplete" means not installed, so the section opens itself for exactly the people who need
  // to act and stays shut for everyone else — the same rule the profile sections already use.
  extension: "incomplete",
  basics: "incomplete", address: "incomplete", links: "incomplete",
  eligibility: "incomplete", eeo: "incomplete", prefs: "incomplete",
  work: "never", education: "never", bank: "never", logins: "never",
};
const OPEN_KEY = "vh_apply_settings_open";

// "2 minutes ago" / "6 days ago". Deliberately coarse: the point is to distinguish "working" from
// "has not run in a week", which is what someone asking "why are my applications stuck?" needs. A
// precise timestamp would invite worry about a gap that just means Chrome was closed overnight.
function relativeSince(iso) {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 90) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

const nonEmpty = (v) => !!String(v ?? "").trim();
const joinDots = (...xs) => xs.filter(nonEmpty).join(" · ");


export default function ApplyProfile() {
  const { embedded } = useOutletContext() || {};
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [pickedResume, setPickedResume] = useState("");
  const [filledKeys, setFilledKeys] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/apply/profile"),
      api.get("/resume/saved").catch(() => ({ data: { resumes: [] } })),
    ])
      .then(([p, r]) => {
        const prof = p.data.profile || {};
        // Work eligibility is US/CA only now — keep just those so no dropdown shows blank.
        const allowed = new Set(WORK_COUNTRIES.map(([c]) => c));
        prof.work_authorization_by_country = Object.fromEntries(
          Object.entries(prof.work_authorization_by_country || {}).filter(([c]) => allowed.has(c)));
        setProfile(prof);
        setResumes(r.data.resumes || []);
      })
      .catch(() => toast.error("Could not load your apply profile"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setField = (k) => (e) => set(k, e.target.value);

  // What each row says when collapsed, and whether it still needs the candidate. "Complete" means
  // the fields the apply engines actually read are present — a missing province failed a real
  // submission, so these mirror what the engines require rather than every field on the form.
  // `profile` is null until the fetch lands, and these run ABOVE the loading guard because they feed
  // hooks — which must not sit behind a conditional return. So read through a safe alias.
  const pf = profile || {};
  const wh = pf.work_history || [], ed = pf.education || [], qa = pf.qa_bank || [];
  const auth = pf.work_authorization_by_country || {};

  // Whether the extension is installed, derived from the timestamp the backend writes whenever it
  // polls /apply/ext/queue. There is no way to ask the browser directly — the content script does
  // not announce itself to the page — so this check-in is the only signal, and it is also why the
  // wording below is "last active" rather than anything implying a live connection.
  const extInstalled = Boolean(pf.ext_last_seen_at);
  const extSeen = relativeSince(pf.ext_last_seen_at);

  const meta = {
    autosubmit: { summary: pf.review_before_submit === false
                    ? "Submits automatically — no review" : "Waits for your review before submitting",
                  complete: null },
    resume:     { summary: (pf.resume_tailoring === "original"
                             ? "Sent as you wrote it" : "Tailored to each job"),
                  complete: null },      // every mode is a valid choice — nothing to complete
    extension:  { summary: extInstalled ? `Connected · ${extSeen}` : "Not installed",
                  complete: extInstalled },
    basics:     { summary: joinDots(pf.full_name, pf.phone, pf.apply_email) || "Not set",
                  complete: nonEmpty(pf.full_name) && nonEmpty(pf.phone) },
    address:    { summary: joinDots(pf.city, pf.province, pf.postal_code) || "Not set",
                  complete: nonEmpty(pf.city) && nonEmpty(pf.province) && nonEmpty(pf.country) },
    links:      { summary: joinDots(pf.linkedin_url && "LinkedIn", pf.github_url && "GitHub",
                                    pf.portfolio_url && "Portfolio") || "None added",
                  complete: nonEmpty(pf.linkedin_url) },
    eligibility:{ summary: Object.keys(auth).length
                    ? `${Object.keys(auth).length} countr${Object.keys(auth).length === 1 ? "y" : "ies"}` : "Not set",
                  complete: Object.keys(auth).length > 0 },
    eeo:        { summary: Object.values(pf.voluntary_disclosures || {}).filter(nonEmpty).length
                    ? "Answered" : "Not answered — we decline on your behalf",
                  complete: null },      // never nag: declining is a legitimate, complete choice
    prefs:      { summary: joinDots(pf.desired_salary, pf.years_experience &&
                                    `${pf.years_experience} yrs`, pf.notice_period) || "Not set",
                  complete: nonEmpty(pf.desired_salary) || nonEmpty(pf.years_experience) },
    work:       { summary: wh.length ? `${wh.length} role${wh.length === 1 ? "" : "s"}` : "None added",
                  complete: wh.length > 0 },
    education:  { summary: ed.length ? `${ed.length} entr${ed.length === 1 ? "y" : "ies"}` : "None added",
                  complete: ed.length > 0 },
    bank:       { summary: qa.length ? `${qa.length} saved answer${qa.length === 1 ? "" : "s"}` : "No saved answers",
                  complete: null },      // grows by itself as you apply — nothing to complete
    logins:     { summary: "Portal accounts we created for you", complete: null },
  };

  // Falls back to the default rule while openMap is still being established, so the first paint is
  // already correct — otherwise every row renders collapsed for a frame and Auto-submit visibly pops.
  const defaultOpen = (key) => SECTION_MODE[key] === "always"
    || (SECTION_MODE[key] === "incomplete" && meta[key].complete === false);

  // Open state. Defaults come from SECTION_MODE, then the candidate's own toggles win and persist —
  // so a section they deliberately closed does not reopen every visit just because it is incomplete.
  const [openMap, setOpenMap] = useState(null);
  useEffect(() => {
    if (loading || openMap) return;
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(OPEN_KEY) || "{}") || {}; } catch { saved = {}; }
    const next = {};
    for (const [key, mode] of Object.entries(SECTION_MODE)) {
      next[key] = key in saved ? !!saved[key]
        : mode === "always" ? true
        : mode === "incomplete" ? meta[key].complete === false
        : false;
    }
    setOpenMap(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const toggle = (key) => setOpenMap((m) => {
    const base = m || Object.fromEntries(Object.keys(SECTION_MODE).map((k) => [k, defaultOpen(k)]));
    const next = { ...base, [key]: !base[key] };
    try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    return next;
  });
  // `meta` must carry an entry per section. A missing one used to throw while RENDERING, which takes
  // the entire settings page down rather than the one row — exactly what happened when the Résumé
  // section was added to SECTION_MODE but not here. Degrade to a blank summary instead.
  const sec = (key) => {
    const m = meta[key] || { summary: "", complete: null };
    return {
      open: openMap ? !!openMap[key] : defaultOpen(key),
      onToggle: () => toggle(key),
      summary: m.summary, complete: m.complete,
    };
  };

  useEffect(() => {
    if (profile && resumes.length && !pickedResume) {
      setPickedResume(profile.default_resume_id || resumes[0]?.resume_id || "");
    }
  }, [profile, resumes]); // eslint-disable-line

  const SECTION_LABEL = {
    full_name: "Name", phone: "Phone", linkedin_url: "LinkedIn", portfolio_url: "Website",
    preferred_locations: "Location", work_history: "Work history", education: "Education",
    years_experience: "Years", qa_bank: "Answer bank",
  };
  const autofill = async () => {
    setAutofilling(true);
    try {
      const { data } = await api.post("/apply/profile/autofill", { resume_id: pickedResume || null });
      const f = data.fields || {};
      setProfile((p) => ({ ...p, ...f, default_resume_id: pickedResume || p.default_resume_id }));
      setFilledKeys(Object.keys(f).filter((k) => SECTION_LABEL[k]));
      toast.success(`Filled from ${data.source_resume || "your résumé"} — review and save.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't auto-fill from that résumé.");
    } finally { setAutofilling(false); }
  };

  // Per-country work authorization (US/CA), multi-row — matches the onboarding wizard.
  const wabc = profile?.work_authorization_by_country || {};
  const authRows = Object.entries(wabc);
  const usedCodes = authRows.map(([c]) => c);
  const commitAuth = (m) => setProfile((p) => ({
    ...p, work_authorization_by_country: m,
    requires_sponsorship: Object.values(m).includes("need_sponsorship"),
  }));
  const setAuthStatus = (code, status) => commitAuth({ ...wabc, [code]: status });
  const changeAuthCountry = (oldCode, newCode) => {
    const m = {};
    for (const [c, s] of authRows) m[c === oldCode ? newCode : c] = s;
    commitAuth(m);
  };
  const addAuthCountry = () => {
    const next = WORK_COUNTRIES.find(([c]) => !(c in wabc));
    if (next) commitAuth({ ...wabc, [next[0]]: "citizen" });
  };
  const removeAuthCountry = (code) => { const m = { ...wabc }; delete m[code]; commitAuth(m); };

  // Answer bank — curated global answers upsert into qa_bank; custom rows edit raw entries;
  // hidden (per-application AI / company-specific) entries are preserved but not shown.
  const bank = profile?.qa_bank || [];
  const curatedAnswer = (q) => (bank.find((x) => norm(x.question) === norm(q)) || {}).answer || "";
  const upsertCurated = (q, answer) => setProfile((p) => {
    const list = [...(p.qa_bank || [])];
    const i = list.findIndex((x) => norm(x.question) === norm(q));
    if (i >= 0) list[i] = { ...list[i], answer, source: list[i].source && list[i].source !== "ai" ? list[i].source : "user" };
    else list.push({ question: q, answer, source: "user" });
    return { ...p, qa_bank: list };
  });
  const customRows = bank.map((qa, idx) => ({ qa, idx }))
    .filter(({ qa }) => !isHiddenQA(qa) && !CURATED_SET.has(norm(qa.question)));
  const setQA = (idx, key, val) =>
    setProfile((p) => ({ ...p, qa_bank: p.qa_bank.map((qa, i) => i === idx ? { ...qa, [key]: val } : qa) }));
  const removeQA = (idx) => setProfile((p) => ({ ...p, qa_bank: p.qa_bank.filter((_, i) => i !== idx) }));
  const addQA = () => setProfile((p) => ({ ...p, qa_bank: [...(p.qa_bank || []), { question: "", answer: "", source: "user" }] }));

  // Work history
  const setWH = (i, k, v) => setProfile((p) => ({ ...p, work_history: (p.work_history || []).map((w, idx) => idx === i ? { ...w, [k]: v } : w) }));
  const addWH = () => setProfile((p) => ({ ...p, work_history: [...(p.work_history || []), { company: "", title: "", location: "", start_date: "", end_date: "", current: false, description: "" }] }));
  const removeWH = (i) => setProfile((p) => ({ ...p, work_history: (p.work_history || []).filter((_, idx) => idx !== i) }));

  // Education
  const setED = (i, k, v) => setProfile((p) => ({ ...p, education: (p.education || []).map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const addED = () => setProfile((p) => ({ ...p, education: [...(p.education || []), { school: "", degree: "", field_of_study: "", start_date: "", end_date: "" }] }));
  const removeED = (i) => setProfile((p) => ({ ...p, education: (p.education || []).filter((_, idx) => idx !== i) }));

  // Voluntary disclosures
  const vd = profile?.voluntary_disclosures || {};
  const setVD = (key, val) => setProfile((p) => ({ ...p, voluntary_disclosures: { ...(p.voluntary_disclosures || {}), [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...profile,
        preferred_locations: (profile.preferred_locations || []).filter(Boolean),
        job_titles: (profile.job_titles || []).filter(Boolean),
        // Drop unanswered curated questions; keep custom + preserved hidden entries.
        qa_bank: (profile.qa_bank || []).filter((qa) =>
          CURATED_SET.has(norm(qa.question)) ? (qa.answer || "").trim() !== "" : true),
      };
      const { data } = await api.put("/apply/profile", payload);
      setProfile((p) => ({ ...p, ...data.profile }));
      toast.success("Apply profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Try again.");
    } finally { setSaving(false); }
  };

  if (loading) return embedded
    ? <Loading label="Loading your apply profile…" />
    : <PageContainer width="mid"><Loading label="Loading your apply profile…" /></PageContainer>;
  if (!profile) return null;

  const emailMismatch = !!(profile.apply_email || "").trim() && !!user?.email
    && (profile.apply_email || "").trim().toLowerCase() !== user.email.toLowerCase();

  const header = (
    <PageHeader
      back={{ to: "/profile", label: "Back to profile" }}
      eyebrow="Autopilot"
      title="Apply profile."
      subtitle="The details application forms ask for, saved once. Autopilot uses these to fill your applications."
      actions={
        <>
          <Button variant="secondary" size="sm" to="/apply/tracker">Applications</Button>
          <Button variant="navy" size="sm" onClick={() => setApplyOpen(true)}><Sparkles className="w-3.5 h-3.5" /> Try one-click apply</Button>
        </>
      }
    />
  );

  const body = (
    <>
      <ApplyModal open={applyOpen} onClose={() => setApplyOpen(false)} />

      {embedded && (
        <div className="mb-5">
          <h1 className="font-display font-black text-2xl text-[#0F172A]">Apply profile</h1>
          <p className="text-sm text-muted-foreground mt-1">Everything from your Autopilot setup — plus the optional details — saved once and reused on every application. Edit anytime.</p>
        </div>
      )}

      {/* Fill-from-résumé hero (full width) */}
      <div className="rounded-2xl border border-[#0025D8]/20 bg-gradient-to-br from-[#0025D8]/[0.07] via-[#0025D8]/[0.03] to-[#7C3AED]/[0.05] p-5 mb-4">
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-[#0025D8] text-white flex items-center justify-center flex-shrink-0 shadow-sm">
            <FileText className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-base text-[#0F172A]">Fill from your résumé</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-3 leading-relaxed">
              Pick a résumé and we'll fill name, work history, education, and more. You review and save.
            </p>
            {resumes.length === 0 ? (
              <a href="/resume-analyzer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0025D8] hover:underline">
                Save a résumé in the Résumé Analyzer first <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
                <select value={pickedResume}
                  onChange={(e) => { setPickedResume(e.target.value); set("default_resume_id", e.target.value); }}
                  className={`${inputClass} sm:max-w-[18rem]`}
                  title="This is also the résumé we submit with your applications">
                  {resumes.map((r) => (
                    <option key={r.resume_id} value={r.resume_id}>{r.name || r.optimized_name || "Résumé"}</option>
                  ))}
                </select>
                <Button variant="navy" size="sm" onClick={autofill} loading={autofilling} className="flex-shrink-0">
                  <Wand2 className="w-3.5 h-3.5" /> Fill from résumé
                </Button>
              </div>
            )}
            {filledKeys.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#059669]"><Check className="w-3 h-3" /> Filled:</span>
                {filledKeys.map((k) => (
                  <span key={k} className="text-[10px] bg-[#059669]/10 text-[#059669] px-1.5 py-0.5 rounded font-medium">{SECTION_LABEL[k]}</span>
                ))}
                <span className="text-[11px] text-muted-foreground">— review below, then Save.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sections as full-width collapsible rows, grouped. Collapsed rows are one line, so a
          complete profile is about a screen; the width goes to the FIELDS inside each open
          section rather than to a second column of whitespace. */}
      <GroupLabel>Automation</GroupLabel>
      <div className="space-y-2">
      <Section {...sec("autosubmit")} icon={ShieldCheck} title="Auto-submit"
  desc="How Autopilot handles the final Submit on each application.">
        <div className="space-y-2.5">
          <Toggle checked={profile.review_before_submit !== false}
      onChange={(v) => set("review_before_submit", v)}
      label="Review each application before it's submitted" />
          <p className="text-[12px] text-muted-foreground leading-relaxed">
      {profile.review_before_submit !== false
        ? "On — Autopilot prepares every application and waits for you to review and click Submit. Safest, and the default."
        : "Off — Autopilot fills and submits automatically, hands-free. You can turn review back on anytime."}
          </p>
        </div>
      </Section>

      {/* The one PERMANENT place to get the extension. It is otherwise offered only where a
          candidate is already blocked — the tracker banner and the per-application status card —
          which means someone who simply wants to install it ahead of time, or to check whether it
          is still working, had nowhere to go. Sits in Automation because that is what it is: the
          half of Autopilot that runs in their own browser. */}
      <Section {...sec("extension")} icon={Puzzle} title="Browser extension"
  desc="Some employers only accept applications sent from your own browser. The extension handles those.">
        <div className="space-y-2.5">
          {extInstalled ? (
            <>
              <div className="flex items-center gap-2 text-[13px] font-semibold text-[#059669]">
                <Check className="w-4 h-4 flex-shrink-0" /> Connected
              </div>
              <p className="text-[12px] text-muted-foreground leading-relaxed">
                Last active {extSeen}. It checks for new applications every couple of minutes while
                Chrome is open, so a gap overnight is normal. If it has been quiet for days, open
                Chrome's extensions page and check it is still enabled.
              </p>
            </>
          ) : (
            <>
              <p className="text-[12px] text-muted-foreground leading-relaxed">
                Not installed. Applications to these employers are prepared and then wait — they
                are submitted automatically once the extension is added, with nothing else for you
                to do.
              </p>
              <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 rounded-xl bg-[#0025D8] hover:bg-[#001FB0] text-white px-3.5 py-2 text-xs font-bold transition-colors">
                <Puzzle className="w-3.5 h-3.5" /> Add to Chrome
              </a>
            </>
          )}
        </div>
      </Section>
      </div>
      <GroupLabel>About you</GroupLabel>
      <div className="space-y-2">
        <Section {...sec("resume")} icon={FileText} title="Résumé"
          desc="How much Autopilot reworks your résumé for each job.">
          <ResumeModePicker value={profile.resume_tailoring || "tailored"}
            onChange={(v) => set("resume_tailoring", v)} />
        </Section>

        <Section {...sec("basics")} icon={User} title="Basics">
          <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full name"><Input value={profile.full_name} onChange={setField("full_name")} placeholder="Your name" /></Field>
        <Field label="Phone" optional><Input value={profile.phone} onChange={setField("phone")} placeholder="+1 555 000 0000" /></Field>
      </div>
      <div>
        <Field label="Application email" optional
          hint="Where we forward recruiter replies and account verifications. Leave blank to use your login email.">
          <Input type="email" value={profile.apply_email || ""} onChange={setField("apply_email")}
            placeholder={user?.email || "you@example.com"} />
        </Field>
        {emailMismatch && (
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300/70 bg-amber-50 px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800 leading-relaxed">
              This is different from your login email (<span className="font-mono">{user?.email}</span>).
              Recruiter emails and account verifications get forwarded here — make sure it's an inbox you check.{" "}
              <button type="button" onClick={() => set("apply_email", user?.email || "")}
                className="font-semibold underline hover:text-amber-900">Use my login email</button>
            </p>
          </div>
        )}
      </div>
          </div>
        </Section>
        <Section {...sec("address")} icon={MapPin} title="Home address" desc="Some ATS pages (e.g. Workday) require a mailing address.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Address line 1" optional className="sm:col-span-2"><Input value={profile.address_line1} onChange={setField("address_line1")} placeholder="200 Bay Street" /></Field>
      <Field label="City" optional><Input value={profile.city} onChange={setField("city")} placeholder="Toronto" /></Field>
      <Field label="Province / State" optional><Input value={profile.province} onChange={setField("province")} placeholder="Ontario" /></Field>
      <Field label="Postal / ZIP" optional><Input value={profile.postal_code} onChange={setField("postal_code")} placeholder="M5J 2J2" /></Field>
      <Field label="Country" optional><Input value={profile.country} onChange={setField("country")} placeholder="Canada" /></Field>
          </div>
        </Section>
        <Section {...sec("links")} icon={Link2} title="Links">
          <div className="space-y-4">
      <Field label="LinkedIn" optional><div className="relative"><Linkedin className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" /><Input value={profile.linkedin_url} onChange={setField("linkedin_url")} placeholder="linkedin.com/in/…" className="pl-9" /></div></Field>
      <Field label="GitHub" optional><div className="relative"><Github className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" /><Input value={profile.github_url} onChange={setField("github_url")} placeholder="github.com/…" className="pl-9" /></div></Field>
      <Field label="Portfolio / website" optional><Input value={profile.portfolio_url} onChange={setField("portfolio_url")} placeholder="https://…" /></Field>
          </div>
        </Section>
      </div>
      <GroupLabel>Eligibility</GroupLabel>
      <div className="space-y-2">
        <Section {...sec("eligibility")} icon={Globe2} title="Work eligibility"
          desc="Add each country you apply in and your status — we answer every job's authorization questions for the right country."
          action={usedCodes.length < WORK_COUNTRIES.length ? <AddBtn onClick={addAuthCountry}>Add</AddBtn> : null}>
          <div className="space-y-2.5">
      {authRows.length === 0 && <p className="text-sm text-muted-foreground italic">Add a country you apply in.</p>}
      {authRows.map(([code, status]) => {
        const opts = WORK_COUNTRIES.filter(([c]) => c === code || !usedCodes.includes(c));
        return (
          <div key={code} className="flex items-center gap-2">
            <Select value={code} onChange={(e) => changeAuthCountry(code, e.target.value)} options={opts} className="flex-1" />
            <Select value={status} onChange={(e) => setAuthStatus(code, e.target.value)} options={AUTH_STATUS.map(([v, l]) => [v, l])} className="flex-1" />
            {authRows.length > 1 && (
              <button onClick={() => removeAuthCountry(code)} className="text-muted-foreground hover:text-[#E11D48] p-1.5 flex-shrink-0" title="Remove"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        );
      })}
          </div>
        </Section>
        <Section {...sec("eeo")} icon={ShieldCheck} title="Voluntary disclosures (EEO)"
          desc="Optional. Blank = “prefer not to answer” — which is what Autopilot submits. We never guess.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Gender" optional><Select value={vd.gender || ""} onChange={(e) => setVD("gender", e.target.value)} options={VD_GENDER} /></Field>
      <Field label="Race / ethnicity" optional><Select value={vd.race_ethnicity || ""} onChange={(e) => setVD("race_ethnicity", e.target.value)} options={VD_RACE} /></Field>
      <Field label="Veteran status" optional><Select value={vd.veteran_status || ""} onChange={(e) => setVD("veteran_status", e.target.value)} options={VD_VETERAN} /></Field>
      <Field label="Disability status" optional><Select value={vd.disability_status || ""} onChange={(e) => setVD("disability_status", e.target.value)} options={VD_DISABILITY} /></Field>
          </div>
        </Section>
        <Section {...sec("prefs")} icon={SlidersHorizontal} title="Preferences">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Location preference"><Select value={profile.location_preference} onChange={setField("location_preference")} options={LOCATION_PREF} /></Field>
      <Field label="Notice period" optional hint="We fill your start date as today + this"><Select value={profile.notice_period} onChange={setField("notice_period")} options={NOTICE} /></Field>
      <Field label="Desired salary" optional><Input value={profile.desired_salary} onChange={setField("desired_salary")} placeholder="$120,000" /></Field>
      <Field label="Years of experience" optional><Input value={profile.years_experience} onChange={setField("years_experience")} placeholder="8" /></Field>
      <div className="sm:col-span-2">
        {/* Roles and locations are the two inputs to the Autopilot grid. Both are editable here, on
            the jobs page itself, and in onboarding — one implementation, three placements. */}
        <RolePicker value={profile.job_titles || []}
          onChange={(v) => set("job_titles", v)} label="Roles you're looking for" />
      </div>
      <div className="sm:col-span-2">
        {/* It replaces a comma-separated text field that split "Toronto, ON, Canada" into THREE
            locations on every save — and it carries the priority order, which the matches grid uses
            to rank and to decide how far to widen the search. */}
        <LocationPicker value={profile.preferred_locations || []}
          onChange={(v) => set("preferred_locations", v)} label="Preferred locations" />
      </div>
      <div className="sm:col-span-2 flex items-center pt-1"><Toggle checked={profile.open_to_relocation} onChange={(v) => set("open_to_relocation", v)} label="Open to relocation" /></div>
          </div>
        </Section>
      </div>
      <GroupLabel>Records</GroupLabel>
      <div className="space-y-2">
        <Section {...sec("work")} icon={Briefcase} title="Work history"
          desc="Used to fill dated experience sections (e.g. Workday). Most recent first."
          action={<AddBtn onClick={addWH}>Add role</AddBtn>}>
          {(profile.work_history || []).length === 0 ? (
      <p className="text-sm text-muted-foreground italic">No work history added.</p>
          ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {profile.work_history.map((w, i) => (
          <div key={i} className="rounded-xl border border-border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input value={w.title} onChange={(e) => setWH(i, "title", e.target.value)} placeholder="Job title" className="flex-1" />
              <button onClick={() => removeWH(i)} className="text-muted-foreground hover:text-[#E11D48] p-1 flex-shrink-0" title="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={w.company} onChange={(e) => setWH(i, "company", e.target.value)} placeholder="Company" />
              <Input value={w.location} onChange={(e) => setWH(i, "location", e.target.value)} placeholder="Location" />
              <Input value={w.start_date} onChange={(e) => setWH(i, "start_date", e.target.value)} placeholder="Start (YYYY-MM)" />
              <Input value={w.end_date} onChange={(e) => setWH(i, "end_date", e.target.value)} placeholder="End (YYYY-MM)" disabled={w.current} />
            </div>
            <Toggle checked={w.current} onChange={(v) => setWH(i, "current", v)} label="I currently work here" />
            <textarea value={w.description} onChange={(e) => setWH(i, "description", e.target.value)} rows={2}
              placeholder="What you did…" className={`${inputClass} resize-none`} />
          </div>
        ))}
      </div>
          )}
        </Section>
        <Section {...sec("education")} icon={GraduationCap} title="Education" action={<AddBtn onClick={addED}>Add</AddBtn>}>
          {(profile.education || []).length === 0 ? (
      <p className="text-sm text-muted-foreground italic">No education added.</p>
          ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {profile.education.map((e, i) => (
          <div key={i} className="rounded-xl border border-border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input value={e.school} onChange={(ev) => setED(i, "school", ev.target.value)} placeholder="School" className="flex-1" />
              <button onClick={() => removeED(i)} className="text-muted-foreground hover:text-[#E11D48] p-1 flex-shrink-0" title="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={e.degree} onChange={(ev) => setED(i, "degree", ev.target.value)} placeholder="Degree" />
              <Input value={e.field_of_study} onChange={(ev) => setED(i, "field_of_study", ev.target.value)} placeholder="Field of study" />
              <Input value={e.start_date} onChange={(ev) => setED(i, "start_date", ev.target.value)} placeholder="Start (YYYY-MM)" />
              <Input value={e.end_date} onChange={(ev) => setED(i, "end_date", ev.target.value)} placeholder="End (YYYY-MM)" />
            </div>
          </div>
        ))}
      </div>
          )}
        </Section>
        <Section {...sec("bank")} icon={MessageSquareText} title="Answer bank"
          desc="Answers that are the same for every employer — filled once and reused. Company-specific questions are written per application and reviewed in the apply flow, so they're not shown here."
          action={<AddBtn onClick={addQA}>Add your own</AddBtn>}>
          <div className="space-y-3">
      {/* Curated global questions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {CURATED_GLOBAL.map(({ q, hint }) => (
          <div key={q} className="rounded-xl border border-border p-3">
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{q}</label>
            <input value={curatedAnswer(q)} onChange={(e) => upsertCurated(q, e.target.value)}
              placeholder={hint} className={inputClass} />
          </div>
        ))}
      </div>

      {/* Custom questions the candidate added */}
      {customRows.length > 0 && (
        <div className="pt-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Your own questions</div>
          <div className="space-y-2">
            {customRows.map(({ qa, idx }) => (
              <div key={idx} className="rounded-xl border border-border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input value={qa.question} onChange={(e) => setQA(idx, "question", e.target.value)} placeholder="Question" className="flex-1" />
                  <button onClick={() => removeQA(idx)} className="text-muted-foreground hover:text-[#E11D48] p-1 flex-shrink-0" title="Remove"><Trash2 className="w-4 h-4" /></button>
                </div>
                <textarea value={qa.answer} onChange={(e) => setQA(idx, "answer", e.target.value)} rows={2}
                  placeholder="Your answer…" className={`${inputClass} resize-none`} />
              </div>
            ))}
          </div>
        </div>
      )}
          </div>
        </Section>
        <CompanyLogins {...sec("logins")} />
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 mt-6 flex justify-end">
        <Button onClick={save} loading={saving} className="shadow-lg"><Save className="w-4 h-4" /> Save profile</Button>
      </div>
    </>
  );

  return embedded
    ? body                                     // the hub already provides the full-width container
    : <PageContainer width="full">{header}{body}</PageContainer>;
}
